const SAMPLE_SIZE = 32;
const OPAQUE_ALPHA = 128;
const LIGHT_LUMINANCE = 0.85;
const LIGHT_SHARE = 0.6;
const OPAQUE_SHARE = 0.98;
const BUCKET = 32;
const CORNERS = [0, SAMPLE_SIZE - 1, SAMPLE_SIZE * (SAMPLE_SIZE - 1), SAMPLE_SIZE * SAMPLE_SIZE - 1];

export type LogoSample = {
	/** Mostly near-white on a transparent background, so it would vanish on a white surface. */
	isLight: boolean;
	/** Nearly every pixel is opaque: a photo, or a logo that carries its own background. */
	isOpaque: boolean;
	/** CSS color shared by all four corners of the untrimmed upload, i.e. the logo's own background. */
	backgroundColor?: string;
};

const bucketKey = (pixels: Uint8ClampedArray, offset: number): string =>
	[0, 1, 2].map(channel => Math.min(255, Math.round(pixels[offset + channel] / BUCKET) * BUCKET)).join(', ');

/** Reads a loaded image on a small canvas. Undefined when the pixels cannot be read (no canvas, or no CORS). */
export const sampleImage = (image: HTMLImageElement): LogoSample | undefined => {
	const canvas = document.createElement('canvas');
	canvas.width = SAMPLE_SIZE;
	canvas.height = SAMPLE_SIZE;
	const context = canvas.getContext('2d');
	if (!context) {
		return undefined;
	}

	let pixels: Uint8ClampedArray;
	try {
		context.drawImage(image, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
		pixels = context.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE).data;
	} catch {
		return undefined;
	}

	let opaque = 0;
	let light = 0;
	for (let i = 0; i < pixels.length; i += 4) {
		if (pixels[i + 3] < OPAQUE_ALPHA) {
			continue;
		}
		opaque += 1;
		light += (0.299 * pixels[i] + 0.587 * pixels[i + 1] + 0.114 * pixels[i + 2]) / 255 > LIGHT_LUMINANCE ? 1 : 0;
	}

	const isOpaque = opaque / (pixels.length / 4) > OPAQUE_SHARE;
	const corners = CORNERS.map(p => (pixels[p * 4 + 3] < OPAQUE_ALPHA ? undefined : bucketKey(pixels, p * 4)));
	const cornerKey = corners[0] && corners.every(key => key === corners[0]) ? corners[0] : undefined;

	return {
		isLight: !isOpaque && opaque > 0 && light / opaque > LIGHT_SHARE,
		isOpaque,
		backgroundColor: cornerKey && `rgb(${cornerKey})`,
	};
};

/** Loads the untrimmed thumbnail of a logo and samples it. Resolves undefined when it cannot be loaded or read. */
export const sampleLogo = (url: string): Promise<LogoSample | undefined> =>
	new Promise(resolve => {
		const image = new Image();
		image.crossOrigin = 'anonymous';
		image.onload = () => resolve(sampleImage(image));
		image.onerror = () => resolve(undefined);
		image.src = url;
	});
